import { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import { Dialog, DialogContent, Box, Typography, IconButton, Button, TextField, MenuItem, CircularProgress } from '@mui/material';

// ── Shared text helpers ──────────────────────────────────────────────────────
const Section = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography sx={{ fontFamily: 'Frank Ruhl Libre, serif', fontWeight: 700, fontSize: '1.05rem', color: '#1e3a5f', mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Box>
);
const P = ({ children }) => (
  <Typography sx={{ fontFamily: 'Heebo, sans-serif', fontSize: '0.88rem', color: '#3a4a5a', lineHeight: 1.9, mb: 1 }}>
    {children}
  </Typography>
);

// ── Terms content ────────────────────────────────────────────────────────────
const TermsContent = () => (
  <Box>
    <Typography sx={{ fontFamily: 'Heebo, sans-serif', fontSize: '0.78rem', color: '#a0a9b4', mb: 3 }}>עודכן: ינואר 2025</Typography>
    <Section title="1. קבלת התנאים">
      <P>השימוש בשירות inorai מהווה הסכמה מלאה לתנאים אלה. "השירות" כולל את ממשק הניהול, מסכי התצוגה, הרכיבים, התבניות וה-API.</P>
    </Section>
    <Section title="2. הרשמה וחשבון">
      <P>נדרשת הרשמה עם פרטים מדויקים. אתם אחראים לשמירת סודיות הסיסמה ולכל פעילות המתבצעת דרך החשבון. אין להעביר חשבון לגורם אחר ללא אישור בכתב.</P>
    </Section>
    <Section title="3. שימוש מותר">
      <P>השירות מיועד לניהול מסכי מידע ציבוריים במוסדות קהילתיים. אסור לפרסם תוכן פוגעני, מטעה, או המפר חוק ישראלי.</P>
    </Section>
    <Section title="4. תשלום וביטול">
      <P>14 יום ניסיון ללא חיוב. לאחר מכן — מסלול חודשי לפי הבחירה. ניתן לבטל בכל עת; הביטול ייכנס לתוקף בסוף תקופת החיוב הנוכחית.</P>
    </Section>
    <Section title="5. קניין רוחני">
      <P>כל הקוד, העיצוב והרכיבים של inorai הם רכוש החברה. התוכן שאתם יוצרים נשאר בבעלותכם.</P>
    </Section>
    <Section title="6. אחריות ומגבלות">
      <P>השירות מסופק AS-IS. inorai לא תישא באחריות לנזקים עקיפים. גובה האחריות המרבית לא תעלה על תשלומי 3 החודשים האחרונים.</P>
    </Section>
    <Section title="7. שינויים ודין חל">
      <P>שינויים מהותיים יועברו במייל 14 יום מראש. הדין חל הוא הדין הישראלי; סמכות שיפוט — תל אביב–יפו.</P>
    </Section>
    <Section title="8. יצירת קשר">
      <P>שאלות: <Box component="a" href="mailto:ineraigroup@gmail.com" sx={{ color: '#9a7209', fontWeight: 600 }}>ineraigroup@gmail.com</Box></P>
    </Section>
  </Box>
);

// ── Privacy content ──────────────────────────────────────────────────────────
const PrivacyContent = () => (
  <Box>
    <Typography sx={{ fontFamily: 'Heebo, sans-serif', fontSize: '0.78rem', color: '#a0a9b4', mb: 3 }}>עודכן: ינואר 2025</Typography>
    <Section title="1. מידע שאנו אוספים">
      <P><strong>מידע שמסרתם:</strong> שם, מייל, שם ארגון, פרטי תשלום (מעובדים ע"י ספק חיצוני — אנחנו לא שומרים מספרי כרטיס).</P>
      <P><strong>מידע טכני:</strong> IP, דפדפן, עמודים שנצפו — לצרכי שיפור השירות.</P>
      <P><strong>תוכן שיצרתם:</strong> לוחות, טקסטים, תמונות שהעלתם לפלטפורמה.</P>
    </Section>
    <Section title="2. שימוש במידע">
      <P>מתן השירות · תמיכה טכנית · שליחת עדכונים חשובים · ניתוח שימוש אנונימי · עמידה בדרישות חוקיות.</P>
    </Section>
    <Section title="3. שיתוף עם צדדים שלישיים">
      <P>אנחנו <strong>לא מוכרים</strong> מידע אישי. ייתכן שיתוף עם ספקי שירות (אחסון, מייל, תשלומים) בכפוף להסכמי סודיות, ועם רשויות כנדרש בחוק.</P>
    </Section>
    <Section title="4. עוגיות">
      <P>עוגיות הכרחיות לתפעול (התחברות, העדפות) ועוגיות אנליטיות אנונימיות. ניתן לחסום בדפדפן.</P>
    </Section>
    <Section title="5. אבטחה ושמירת נתונים">
      <P>HTTPS, גישה מוגבלת, עדכוני אבטחה שוטפים. נתוני חשבון שנסגר נמחקים תוך 90 יום.</P>
    </Section>
    <Section title="6. זכויותיכם">
      <P>עיון · תיקון · מחיקה · ניידות מידע. לממש: <Box component="a" href="mailto:ineraigroup@gmail.com" sx={{ color: '#9a7209', fontWeight: 600 }}>ineraigroup@gmail.com</Box></P>
    </Section>
  </Box>
);

// ── Contact form ─────────────────────────────────────────────────────────────
const SUBJECTS = [
  'הצטרפות לתכנית החלוצים',
  'שאלה לגבי המסלולים',
  'בקשת הצעת מחיר לחבילת חומרה',
  'תמיכה טכנית',
  'שאלה כללית',
  'אחר',
];

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Heebo, sans-serif', fontSize: '0.9rem', borderRadius: 2,
    '& fieldset': { borderColor: 'rgba(30,58,95,0.2)' },
    '&:hover fieldset': { borderColor: '#1e3a5f' },
    '&.Mui-focused fieldset': { borderColor: '#9a7209' },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Heebo, sans-serif', fontSize: '0.88rem',
    '&.Mui-focused': { color: '#9a7209' },
  },
};

const ContactContent = ({ prefill, onClose }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    synagogue: '',
    subject: prefill?.subject || '',
    message: prefill?.message || '',
  });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (prefill) {
      setForm(f => ({
        ...f,
        subject: prefill.subject || f.subject,
        message: prefill.message || f.message,
      }));
    }
  }, [prefill]);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          from_name: form.name,
          from_email: form.email,
          synagogue: form.synagogue || '—',
          subject: form.subject,
          message: form.message,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
      );
      setSent(true);
    } catch (err) {
      console.error('EmailJS error:', err);
      alert('שגיאה בשליחה, נסו שוב או פנו ישירות ל-ineraigroup@gmail.com');
    } finally {
      setSending(false);
    }
  };

  if (sent) return (
    <Box sx={{ textAlign: 'center', py: 5 }}>
      <Typography sx={{ fontFamily: 'Frank Ruhl Libre, serif', fontSize: '1.6rem', fontWeight: 900, color: '#1e3a5f', mb: 1.5 }}>
        תודה על פנייתכם
      </Typography>
      <Typography sx={{ fontFamily: 'Heebo, sans-serif', fontSize: '0.95rem', color: '#5a6978', lineHeight: 1.8, mb: 3 }}>
        ההודעה נשלחה בהצלחה — נחזור אליכם בהקדם.
      </Typography>
      <Button onClick={onClose} sx={{ color: '#9a7209', fontFamily: 'Heebo, sans-serif', fontWeight: 600, textTransform: 'none' }}>
        סגור
      </Button>
    </Box>
  );

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.2 }}>
      {prefill?.planName && (
        <Box sx={{ bgcolor: 'rgba(154,114,9,0.07)', border: '1px solid rgba(154,114,9,0.2)', borderRadius: 2, px: 2.5, py: 1.5 }}>
          <Typography sx={{ fontFamily: 'Heebo, sans-serif', fontSize: '0.85rem', color: '#1e3a5f', fontWeight: 600, mb: 0.3 }}>
            מסלול נבחר: {prefill.planName}
          </Typography>
          <Typography sx={{ fontFamily: 'Heebo, sans-serif', fontSize: '0.8rem', color: '#7a8898', lineHeight: 1.6 }}>
            בקרוב תהיה סליקה אונליין לרכישה עצמאית — בינתיים נסדר הכל ישירות מולכם.
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <TextField label="שם מלא" value={form.name} onChange={set('name')} required fullWidth sx={fieldSx} />
        <TextField label="כתובת מייל" type="email" value={form.email} onChange={set('email')} required fullWidth sx={fieldSx} />
      </Box>

      <TextField label="שם בית הכנסת / הארגון (אופציונלי)" value={form.synagogue} onChange={set('synagogue')} fullWidth sx={fieldSx} />

      <TextField select label="נושא הפנייה" value={form.subject} onChange={set('subject')} required fullWidth sx={fieldSx}>
        {SUBJECTS.map(s => (
          <MenuItem key={s} value={s} sx={{ fontFamily: 'Heebo, sans-serif', fontSize: '0.88rem' }}>{s}</MenuItem>
        ))}
      </TextField>

      <TextField label="הודעה" value={form.message} onChange={set('message')} required fullWidth multiline rows={4} sx={fieldSx} />

      <Button type="submit" variant="contained" fullWidth disabled={sending} sx={{
        bgcolor: '#9a7209', color: 'white', fontFamily: 'Heebo, sans-serif', fontWeight: 700,
        fontSize: '0.95rem', py: 1.4, borderRadius: 2,
        boxShadow: '0 6px 20px rgba(154,114,9,0.3)',
        '&:hover': { bgcolor: '#b8860b' },
        '&.Mui-disabled': { bgcolor: 'rgba(154,114,9,0.4)', color: 'white' },
      }}>
        {sending ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'שלח הודעה'}
      </Button>

      <Typography sx={{ fontFamily: 'Heebo, sans-serif', fontSize: '0.75rem', color: '#a0a9b4', textAlign: 'center' }}>
        הפנייה תישלח ישירות אל ineraigroup@gmail.com
      </Typography>

      <Typography sx={{ fontFamily: 'Heebo, sans-serif', fontSize: '0.8rem', color: '#7a8898', textAlign: 'center', mt: 0.5 }}>
        או התקשרו אלינו:{' '}
        <Box component="a" href="tel:0548467831" sx={{ color: '#9a7209', fontWeight: 600, textDecoration: 'none', direction: 'ltr', display: 'inline-block' }}>
          054-846-7831
        </Box>
      </Typography>
    </Box>
  );
};

// ── Modal titles ─────────────────────────────────────────────────────────────
const TITLES = {
  terms:   'תנאי שימוש',
  privacy: 'מדיניות פרטיות',
  contact: 'צור קשר',
};

// ── Main modal ───────────────────────────────────────────────────────────────
const LandingModal = ({ open, mode, prefill, onClose }) => {
  if (!mode) return null;
  const isContact = mode === 'contact';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isContact ? 'sm' : 'md'}
      fullWidth
      PaperProps={{
        sx: {
          direction: 'rtl',
          borderRadius: 3,
          maxHeight: '88vh',
          m: { xs: 1.5, sm: 3 },
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: { xs: 2.5, sm: 3.5 },
        pt: 3,
        pb: 1.5,
        borderBottom: '1px solid rgba(30,58,95,0.08)',
      }}>
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, serif',
          fontSize: { xs: '1.2rem', sm: '1.5rem' },
          fontWeight: 900,
          color: '#1e3a5f',
        }}>
          {TITLES[mode]}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: '#8a94a0', '&:hover': { color: '#1e3a5f', bgcolor: 'rgba(30,58,95,0.06)' } }}
        >
          <Box component="span" sx={{ fontSize: '1.1rem', lineHeight: 1 }}>✕</Box>
        </IconButton>
      </Box>

      <DialogContent sx={{ px: { xs: 2.5, sm: 3.5 }, py: 3 }}>
        {mode === 'terms'   && <TermsContent />}
        {mode === 'privacy' && <PrivacyContent />}
        {mode === 'contact' && <ContactContent prefill={prefill} onClose={onClose} />}
      </DialogContent>
    </Dialog>
  );
};

export default LandingModal;
